/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { useCallback, useEffect, useRef, useState, type FC } from "react"
import { Check, Info, Plus, X } from "lucide-react"
import { viewControlsBar } from "../../styles"
import { alpha, theme } from "../../styles/theme/theme"
import { SearchableDropdown } from "./SearchableDropdown"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../ui/select"
import { toColumnLabel } from "../../utils/format"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"
import type { FilterByProps, FilterEntry, FilterOperator } from "./types"
import {
  chip,
  chipRow,
  chipDelete,
  dropArrow,
  bareSelectTrigger,
  flexRow,
  dropdownContentProps,
  useControlDisabled,
} from "./shared"

// ─── FilterBy-specific styles ────────────────────────────────────────────────

const operatorText = css`
  font-size: ${theme.typography.fontSize.xs};
  line-height: 20px;
  color: ${alpha(theme.colors.foreground, 0.87)};
  white-space: nowrap;
`

const filterDeleteBtn = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: ${alpha(theme.colors.foreground, 0.08)};
  cursor: pointer;
  flex-shrink: 0;
  outline: none;
  &:hover { background: ${alpha(theme.colors.foreground, 0.04)}; }
  &:focus, &:focus-visible { outline: none; box-shadow: none; }
`

const filterTextInput = css`
  font-size: ${theme.typography.fontSize.xs};
  border: none;
  border-bottom: 1px solid ${alpha(theme.colors.foreground, 0.42)};
  border-radius: 0;
  padding: 0 0 2px;
  background: transparent;
  color: ${alpha(theme.colors.foreground, 0.87)};
  outline: none;
  min-width: 80px;
  &:hover {
    border-bottom: 2px solid ${alpha(theme.colors.foreground, 0.87)};
    padding-bottom: 1px;
  }
  &:focus {
    border-bottom: 2px solid ${alpha(theme.colors.foreground, 0.87)};
    padding-bottom: 1px;
  }
`

// ─── FilterRow ───────────────────────────────────────────────────────────────

/** Single filter row — buffers changes locally until the green checkmark is clicked. */
const FilterRow: FC<{
  filter: FilterEntry
  index: number
  columns: FilterByProps["columns"]
  disabled: boolean
  onChange: (index: number, filter: FilterEntry) => void
  onDelete: (index: number) => void
}> = ({ filter, index, columns, disabled, onChange, onDelete }) => {
  // Draft state — local edits that haven't been applied yet
  const [draft, setDraft] = useState<FilterEntry>(filter)
  const [column, operator, values] = draft

  // Track whether draft differs from the committed (applied) filter,
  // or if the committed filter has no values (nothing meaningful applied yet)
  const hasPendingChanges =
    draft[0] !== filter[0] ||
    draft[1] !== filter[1] ||
    draft[2].length !== filter[2].length ||
    draft[2].some((v, i) => v !== filter[2][i])
  const isDirty = hasPendingChanges || filter[2].length === 0

  // Sync draft when the committed filter changes externally (e.g. reset)
  const filterRef = useRef(filter)
  useEffect(() => {
    if (
      filter[0] !== filterRef.current[0] ||
      filter[1] !== filterRef.current[1] ||
      filter[2].length !== filterRef.current[2].length ||
      filter[2].some((v, i) => v !== filterRef.current[2][i])
    ) {
      filterRef.current = filter
      setDraft(filter)
    }
  }, [filter])

  const colDef = columns.find((c) => c.value === column)
  const sortedOptions = colDef
    ? [...colDef.options].filter(Boolean).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    : []
  const available = sortedOptions.filter((o) => !values.includes(o))

  const [inputValue, setInputValue] = useState(
    operator === "contains" ? (values[0] ?? "") : ""
  )

  const changeColumn = useCallback(
    (newCol: string) => setDraft([newCol, operator, values]),
    [operator, values],
  )

  const changeOperator = useCallback(
    (newOp: string) => setDraft([column, newOp as FilterOperator, values]),
    [column, values],
  )

  const addValue = useCallback(
    (val: string) => {
      if (!values.includes(val)) {
        setDraft([column, operator, [...values, val]])
      }
    },
    [column, operator, values],
  )

  const removeValue = useCallback(
    (val: string) => {
      setDraft([column, operator, values.filter((v) => v !== val)])
    },
    [column, operator, values],
  )

  const applyFilter = useCallback(() => {
    // A blank "contains" box commits no values (not [""]) so it's dropped from
    // push-down like any other empty filter — never sent to the engine as a
    // no-op `contains ""` predicate or shown in the export label as `… []`.
    const applied: FilterEntry =
      operator === "contains" ? [column, operator, inputValue ? [inputValue] : []] : draft
    onChange(index, applied)
  }, [index, draft, column, operator, inputValue, onChange])

  return (
    <div css={flexRow}>
      {/* Column selector — searchable dropdown with chip display, underlined */}
      <div css={[viewControlsBar.underline, css`min-width: 30px; margin-right: ${theme.spacing.sm};`]}>
        <SearchableDropdown
          options={columns.map((c) => ({ value: c.value, label: c.label }))}
          onSelect={changeColumn}
          selectedValues={[column]}
          disabled={disabled}
        >
          <div css={flexRow}>
            <span css={chip}>{colDef?.label ?? toColumnLabel(column)}</span>
            <span css={dropArrow} />
          </div>
        </SearchableDropdown>
      </div>

      {/* Operator selector — Select with text renderValue, underlined */}
      <div css={[viewControlsBar.underline, css`min-width: 30px; margin-right: ${theme.spacing.sm};`]}>
        <Select value={operator} onValueChange={changeOperator} disabled={disabled}>
          <SelectTrigger css={bareSelectTrigger}>
            <span css={operatorText}>{operator}</span>
            <span css={dropArrow} />
          </SelectTrigger>
          <SelectContent {...dropdownContentProps}>
            <SelectItem value="in">in</SelectItem>
            <SelectItem value="not in">not in</SelectItem>
            <SelectItem value="contains">contains</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Values — searchable multi-select (in/not in) or text input (contains) */}
      {operator !== "contains" && sortedOptions.length > 0 && (
        <div css={[viewControlsBar.underline, css`min-width: 30px; margin-right: ${theme.spacing.sm};`]}>
          <div css={chipRow}>
            {values.map((v) => (
              <span key={v} css={chip}>
                {v}
                <button
                  css={chipDelete}
                  onClick={() => removeValue(v)}
                  disabled={disabled}
                  type="button"
                >
                  <X css={css`width: 14px; height: 14px;`} />
                </button>
              </span>
            ))}
          </div>
          <SearchableDropdown
            options={available.map((o) => ({ value: o, label: o }))}
            onSelect={addValue}
            selectedValues={values}
            disabled={disabled || available.length === 0}
          >
            <span css={dropArrow} />
          </SearchableDropdown>
        </div>
      )}

      {operator === "contains" && (
        <input
          css={[filterTextInput, css`margin-right: ${theme.spacing.sm};`]}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={disabled}
        />
      )}

      {/* Apply button — green checkmark, commits the draft filter */}
      <button
        css={[
          filterDeleteBtn,
          css`
            margin-right: ${theme.spacing.xs};
            background: ${isDirty ? theme.colors.filter.pendingBg : theme.colors.filter.appliedBg};
            cursor: ${isDirty ? "pointer" : "default"};
          `,
        ]}
        onClick={applyFilter}
        disabled={disabled}
        type="button"
        title={isDirty ? "Apply filter" : "Filter applied"}
      >
        <Check css={css`width: 12px; height: 12px; color: ${isDirty ? theme.colors.filter.pendingIcon : theme.colors.filter.applied};`} />
      </button>

      {/* Delete button — red X, matches atomic-webapp's MinimalButton + CloseIcon */}
      <button
        css={[filterDeleteBtn, css`margin-right: ${theme.spacing.sm};`]}
        onClick={() => onDelete(index)}
        disabled={disabled}
        type="button"
        title="Remove filter"
      >
        <X css={css`width: 12px; height: 12px; color: ${theme.colors.filter.delete};`} />
      </button>
    </div>
  )
}

// ─── FilterBy ────────────────────────────────────────────────────────────────

export const FilterBy: FC<FilterByProps> = ({
  filters,
  onFiltersChange,
  columns,
  disabled: ownDisabled = false,
  tooltip = "You can filter by multiple values in same filter by selecting them in the dropdown. Filters are additive, not conditional.",
}) => {
  const disabled = useControlDisabled(ownDisabled)
  const addFilter = useCallback(() => {
    if (columns.length === 0) return
    onFiltersChange([...filters, [columns[0].value, "in", []]])
  }, [filters, onFiltersChange, columns])

  const changeFilter = useCallback(
    (idx: number, updated: FilterEntry) => {
      const next = [...filters] as FilterEntry[]
      next[idx] = updated
      onFiltersChange(next)
    },
    [filters, onFiltersChange],
  )

  const removeFilter = useCallback(
    (idx: number) => {
      onFiltersChange(filters.filter((_, i) => i !== idx))
    },
    [filters, onFiltersChange],
  )

  return (
    <div css={viewControlsBar.widget}>
      <div css={viewControlsBar.label}>
        Filter by
        <Tooltip>
          <TooltipTrigger asChild>
            <span css={css`display: inline-flex; margin-left: ${theme.spacing.xs}; cursor: help;`}>
              <Info css={css`width: 14px; height: 14px; color: ${alpha(theme.colors.foreground, 0.3)};`} />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={4} css={css`max-width: 260px;`}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </div>
      <div css={css`display: flex; flex-wrap: wrap; align-items: center; min-height: 23px;`}>
        {filters.map((f, idx) => (
          <FilterRow
            key={`${f[0]}-${f[1]}-${idx}`}
            filter={f}
            index={idx}
            columns={columns}
            disabled={disabled}
            onChange={changeFilter}
            onDelete={removeFilter}
          />
        ))}

        <button
          css={viewControlsBar.addBtn}
          onClick={addFilter}
          disabled={disabled || columns.length === 0}
          type="button"
          title="Add filter"
        >
          <Plus css={css`width: 12px; height: 12px;`} />
        </button>
      </div>
    </div>
  )
}
